import TextField from '@app/src/components/Ui/TextField'
import type { QuestionType } from '@app/src/types/reporting'
import type { QuestionAnswerClassificationRule } from '@app/src/types/resourceExplorer'
import {
  AnswerClassificationValue,
  AnswerRuleType,
  AssessmentType,
  QuestionAnswerRank,
  QuestionAnswerRankScoringType,
} from '@app/src/types/resourceExplorer'
import { InfoOutlined } from '@mui/icons-material'
import { Alert, Box, Divider, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { DEFAULT_ASSESSMENT_POINTS } from '../../../components/Configurations/SustainabilityLibraryPages/Assessments/utils/constants'
import Select from '../../../components/Form/Select/ControlledSelect'
import AdvancedClassification from './AdvancedClassification'

type QuestionAnswerClassificationRuleSaveModel = QuestionAnswerClassificationRule & {
  disabled?: boolean
}

type EditQuestionAnswerClassificationProps = {
  questionTypeId: QuestionType
  assessmentType?: AssessmentType
}

type ClassificationRulesByRank = Record<QuestionAnswerRank, QuestionAnswerClassificationRuleSaveModel[] | undefined>

const CLASSIFICATION_VALUES = [
  AnswerClassificationValue.Answered,
  AnswerClassificationValue.NotApplicable,
  AnswerClassificationValue.NotAvailable,
  AnswerClassificationValue.Excluded,
  AnswerClassificationValue.Other,
] as const

/**
 * Collects all classification values that are already assigned to other ranks.
 * A value can only be assigned to one rank at a time, so we disable it in other rank selectors.
 */
function getValuesUsedByOtherRanks(
  rulesByRank: ClassificationRulesByRank | undefined,
  currentRank: QuestionAnswerRank,
): Set<AnswerClassificationValue> {
  if (!rulesByRank) {
    return new Set()
  }

  const usedValues = new Set<AnswerClassificationValue>()

  for (const [rank, rules] of Object.entries(rulesByRank)) {
    if (rank === currentRank || rank === QuestionAnswerRank.NotSet || !rules) {
      continue
    }

    for (const rule of rules) {
      if (rule.value !== undefined && rule.ruleType === AnswerRuleType.AnswerStatus) {
        usedValues.add(rule.value as AnswerClassificationValue)
      }
    }
  }

  return usedValues
}

const EditQuestionAnswerClassification: React.FC<EditQuestionAnswerClassificationProps> = ({
  questionTypeId,
  assessmentType = AssessmentType.StandardAssessment,
}) => {
  const { formatMessage } = useIntl()
  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext()

  const watchImpactValueType = watch('impactValueType')
  const classificationRulesByRank: ClassificationRulesByRank | undefined = watch('questionAnswerClassificationRules')

  const selectableRanks = useMemo(
    () => Object.values(QuestionAnswerRank).filter(rank => rank !== QuestionAnswerRank.NotSet),
    [],
  )

  useEffect(() => {
    if (assessmentType === AssessmentType.StandardAssessment) {
      setValue('impactValueType', QuestionAnswerRankScoringType.Default)
    }
  }, [assessmentType])

  useEffect(() => {
    if (watchImpactValueType === QuestionAnswerRankScoringType.Default) {
      setValue('points.veryPositive', DEFAULT_ASSESSMENT_POINTS.VERY_POSITIVE)
      setValue('points.positive', DEFAULT_ASSESSMENT_POINTS.POSITIVE)
      setValue('points.negative', DEFAULT_ASSESSMENT_POINTS.NEGATIVE)
      setValue('points.negativeAdverse', DEFAULT_ASSESSMENT_POINTS.NEGATIVE_ADVERSE)
      setValue('points.neutral', DEFAULT_ASSESSMENT_POINTS.NEUTRAL)
    }
  }, [watchImpactValueType, setValue])

  return (
    <Stack spacing={3}>
      {assessmentType === AssessmentType.Flexible && (
        <Box width="40%">
          <Select
            name="impactValueType"
            control={control}
            size="small"
            defaultValue={QuestionAnswerRankScoringType.Default}
            fieldLabel={formatMessage({ id: 'assessments.impactValues' })}
            options={[
              {
                label: formatMessage({ id: 'general.default' }),
                value: QuestionAnswerRankScoringType.Default,
              },
              {
                label: formatMessage({ id: 'configurations.custom' }),
                value: QuestionAnswerRankScoringType.Custom,
              },
            ]}
          />
        </Box>
      )}
      {watchImpactValueType === QuestionAnswerRankScoringType.Custom && (
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            type="number"
            name="points.veryPositive"
            defaultValue={DEFAULT_ASSESSMENT_POINTS.VERY_POSITIVE}
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
            label={formatMessage({ id: 'templateBuilder.answerClassifications.classificationsRanks.VeryPositive' })}
            error={!!errors.points?.veryPositive}
            helperText={errors.points?.veryPositive?.message}
          />
          <TextField
            size="small"
            type="number"
            name="points.positive"
            defaultValue={DEFAULT_ASSESSMENT_POINTS.POSITIVE}
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
            label={formatMessage({ id: 'templateBuilder.answerClassifications.classificationsRanks.Positive' })}
            error={!!errors.points?.positive}
            helperText={errors.points?.positive?.message}
          />
          <TextField
            size="small"
            type="number"
            name="points.negative"
            defaultValue={DEFAULT_ASSESSMENT_POINTS.NEGATIVE}
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
            label={formatMessage({ id: 'templateBuilder.answerClassifications.classificationsRanks.Negative' })}
            error={!!errors.points?.negative}
            helperText={errors.points?.negative?.message}
          />
          <TextField
            size="small"
            type="number"
            name="points.negativeAdverse"
            defaultValue={DEFAULT_ASSESSMENT_POINTS.NEGATIVE_ADVERSE}
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
            label={formatMessage({ id: 'templateBuilder.answerClassifications.classificationsRanks.NegativeAdverse' })}
            error={!!errors.points?.negativeAdverse}
            helperText={errors.points?.negativeAdverse?.message}
          />
        </Stack>
      )}
      {assessmentType === AssessmentType.Flexible && (
        <>
          <Divider />
          <Alert severity="warning">
            <Typography>{formatMessage({ id: 'assessments.impactValuesWarning' })}</Typography>
          </Alert>
        </>
      )}
      {assessmentType === AssessmentType.StandardAssessment && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {formatMessage({ id: 'templateBuilder.answerClassifications.title' })}
          </Typography>
          <Box display="flex" alignItems="center">
            <InfoOutlined fontSize="small" color="action" />
            <Typography variant="caption" color="textSecondary" ml={1}>
              {formatMessage({ id: 'templateBuilder.answerClassifications.subtitle' })}
            </Typography>
          </Box>
        </Box>
      )}
      {selectableRanks.map(rank => {
        const disabledValues = getValuesUsedByOtherRanks(classificationRulesByRank, rank)

        const options = CLASSIFICATION_VALUES.map(classificationValue => ({
          label: formatMessage({
            id: `templateBuilder.answerClassifications.classificationsValues.${classificationValue}`,
          }),
          value: {
            rank,
            ruleType: AnswerRuleType.AnswerStatus,
            value: classificationValue,
          },
          disabled: disabledValues.has(classificationValue),
        }))

        return (
          <Box key={rank}>
            <Select<QuestionAnswerClassificationRuleSaveModel>
              name={`questionAnswerClassificationRules[${rank}]`}
              control={control}
              fieldLabel={formatMessage({
                id: `templateBuilder.answerClassifications.classificationsRanks.${rank}`,
              })}
              size="small"
              multiple
              options={options}
              findSelectedValue={(value, option) => value?.value === option?.value?.value}
              onInputChange={(_e, value) => {
                if (!value) setValue(`questionAnswerClassificationRules[${rank}]`, undefined)
              }}
            />
          </Box>
        )
      })}
      <AdvancedClassification questionTypeId={questionTypeId} />
    </Stack>
  )
}

export default EditQuestionAnswerClassification
